import type { Uuid } from "@/domains/global/identifiers";

/**
 * The stage a recording's pipeline is currently in. Stages advance strictly
 * in the order declared in `audio-pipeline.stages.ts`. The conditional stages
 * (`UploadSource`, `RetranscribeFromSource`) are entered only when their
 * branch needs them. Handoff audio merge happens asynchronously on the backend
 * from the note-suggestion sync operation, so this client pipeline never waits
 * on audio-file canonicalization.
 */
export enum Stage {
  Recording = "recording",
  Finalize = "finalize",
  ValidateAudio = "validate-audio",
  ResolveTranscriptSource = "resolve-transcript-source",
  UploadSource = "upload-source",
  RetranscribeFromSource = "retranscribe-from-source",
  Transform = "transform",
  Done = "done",
}

export type StageStatus = "idle" | "running" | "done";

/**
 * Terminal classifications a recording can land in. Exactly one is set once
 * `stage === Done`. UI copy, retry semantics, and telemetry all key off this.
 */
export enum TerminalOutcome {
  Success = "success",
  NoUsableAudioFound = "no-usable-audio-found",
  AudioArtifactMissing = "audio-artifact-missing",
  UploadFailed = "upload-failed",
  RetranscribeFailed = "retranscribe-failed",
  TransformFailed = "transform-failed",
  UnexpectedFailed = "unexpected-failed",
}

/**
 * Output of the final-audio-validation step. `no-usable-audio-found` and
 * `artifact-missing` both terminate the pipeline locally (no upload, no
 * backend retranscribe).
 */
export type ValidationResult =
  | { kind: "valid" }
  | { kind: "no-usable-audio-found"; reason: "silent" | "too-short" }
  | { kind: "artifact-missing" };

/**
 * Output of the transcript-source resolver. `local` skips upload+retranscribe
 * stages; `retranscribe` requires the audio to be uploaded first, then
 * backend-transcribed.
 */
export type TranscriptSourceResolution = "local" | "retranscribe";

export type StageProgress = {
  stage: Stage;
  /** 0–100, or null when progress is indeterminate. */
  percent: number | null;
};

export type PipelineDiagnostic = {
  /** Short identifier for the cause, e.g. "network-timeout" or "silent-buffer". */
  kind: string;
  /** User-safe message. UI may surface this directly. */
  message: string;
};

/**
 * The single object UI reads to render pipeline state. No other store should
 * project pipeline status for UI consumption.
 */
export type PipelineSnapshot = {
  audioRecordingId: Uuid;
  stage: Stage;
  stageStatus: StageStatus;
  terminalOutcome: TerminalOutcome | null;
  progress: StageProgress | null;
  diagnostic: PipelineDiagnostic | null;
};
